import { trackEvent } from "@/lib/analytics";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useFoundingCreditWindow } from "@/hooks/useFoundingCreditWindow";
import { useIndexingOpen } from "@/hooks/useIndexingOpen";
import { GATES_SUSPENDED } from "@/lib/accessMode";
import { verifyCreditPurchase } from "@/lib/payments.functions";

/**
 * Ensure a valid (non-expired) auth session before invoking a protected edge
 * function. Refreshes a near-expiry token so checkout/verify never fail with a
 * cryptic 401 that users experience as "couldn't purchase credits".
 */
async function ensureValidSession() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error("Please sign in to buy credits.");
  }
  const expiresAt = (session.expires_at ?? 0) * 1000;
  if (expiresAt - Date.now() < 60_000) {
    const { data, error } = await supabase.auth.refreshSession();
    if (error || !data.session) {
      throw new Error("Your session has expired. Please sign in again.");
    }
  }
}

/**
 * Credits are the platform's reveal currency.
 * - Revealing a prediction (pick, odds, confidence, analysis) costs 2 credits.
 * - Unlocking a prediction's strategy insights costs 1 additional credit.
 * Balance is split into `daily_credits` (the monthly 5-credit allowance, replaced
 * every 30 days, never accumulates) and `persistent_credits` (purchased / earned, never
 * reset). All balance changes happen server-side through SECURITY DEFINER RPCs.
 */
export interface CreditWallet {
  balance: number;
  daily_credits: number;
  persistent_credits: number;
  purchased_credits: number;
  earned_credits: number;
  last_daily_grant_at: string | null;
}

export interface CreditTransaction {
  id: string;
  amount: number;
  reason: string;
  prediction_id: string | null;
  created_at: string;
}

export function useCreditWallet() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["credit-wallet", user?.id],
    enabled: !!user,
    queryFn: async (): Promise<CreditWallet> => {
      if (!user) throw new Error("Sign in required");
      const { data, error } = await supabase
        .from("credit_wallets")
        .select(
          "balance, daily_credits, persistent_credits, purchased_credits, earned_credits, last_daily_grant_at",
        )
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return (
        (data as CreditWallet) ?? {
          balance: 0,
          daily_credits: 0,
          persistent_credits: 0,
          purchased_credits: 0,
          earned_credits: 0,
          last_daily_grant_at: null,
        }
      );
    },
  });
}
/** Earned credits still in their 21-day hold. */
export function usePendingCredits() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["pending-credits", user?.id],
    enabled: !!user,
    staleTime: 60_000,
    queryFn: async (): Promise<{ total: number; next_available_at: string | null }> => {
      const { data, error } = await supabase.rpc("my_pending_credits");
      if (error) throw error;
      const d = (data ?? {}) as { total?: number; next_available_at?: string | null };
      return { total: Number(d.total ?? 0), next_available_at: d.next_available_at ?? null };
    },
  });
}
/** Backwards-compatible alias. */
export const useCoinWallet = useCreditWallet;

/** Set of prediction ids the current user has revealed (permanent). */
export function usePredictionUnlocks() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["prediction-unlocks", user?.id],
    enabled: !!user,
    queryFn: async (): Promise<Set<string>> => {
      if (!user) return new Set();
      const { data, error } = await supabase
        .from("prediction_unlocks")
        .select("prediction_id")
        .eq("user_id", user.id);
      if (error) throw error;
      return new Set((data ?? []).map((r) => r.prediction_id as string));
    },
  });
}

/** Set of prediction ids whose strategy insights the user has unlocked. */
export function useStrategyUnlocks() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["strategy-unlocks", user?.id],
    enabled: !!user,
    queryFn: async (): Promise<Set<string>> => {
      if (!user) return new Set();
      const { data, error } = await supabase
        .from("prediction_strategy_unlocks")
        .select("prediction_id")
        .eq("user_id", user.id);
      if (error) throw error;
      return new Set((data ?? []).map((r) => r.prediction_id as string));
    },
  });
}

export function useCreditTransactions(limit = 50) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["credit-transactions", user?.id, limit],
    enabled: !!user,
    queryFn: async (): Promise<CreditTransaction[]> => {
      if (!user) return [];
      if (limit === Infinity) {
        const transactions: CreditTransaction[] = [];
        for (let offset = 0; ; offset += 500) {
          const { data, error } = await supabase
            .from("credit_transactions")
            .select("id, amount, reason, prediction_id, created_at")
            .eq("user_id", user.id)
            .order("created_at", { ascending: false })
            .order("id", { ascending: false })
            .range(offset, offset + 499);
          if (error) throw error;
          transactions.push(...(data ?? []));
          if (!data || data.length < 500) return transactions;
        }
      }
      const { data, error } = await supabase
        .from("credit_transactions")
        .select("id, amount, reason, prediction_id, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data as CreditTransaction[]) ?? [];
    },
  });
}
export const useCoinTransactions = useCreditTransactions;

function invalidateWallet(qc: ReturnType<typeof useQueryClient>, userId?: string) {
  qc.invalidateQueries({ queryKey: ["credit-wallet", userId] });
  qc.invalidateQueries({ queryKey: ["credit-transactions", userId] });
}

/** Spend 2 credits to permanently reveal a prediction (idempotent server-side). */
export function useUnlockPrediction() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (predictionId: string) => {
      const { data, error } = await supabase.rpc("spend_credits_to_reveal", {
        _prediction_id: predictionId,
      });
      if (error) throw error;
      return data as { unlocked: boolean; already: boolean; balance: number };
    },
    onSuccess: () => {
      invalidateWallet(queryClient, user?.id);
      queryClient.invalidateQueries({ queryKey: ["prediction-unlocks", user?.id] });
    },
  });
}

/** Spend 1 credit to unlock a prediction's strategy insights. */
export function useUnlockStrategy() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (predictionId: string) => {
      const { data, error } = await supabase.rpc("spend_credit_for_strategy", {
        _prediction_id: predictionId,
      });
      if (error) throw error;
      return data as { unlocked: boolean; already: boolean; balance: number };
    },
    onSuccess: () => {
      invalidateWallet(queryClient, user?.id);
      queryClient.invalidateQueries({ queryKey: ["strategy-unlocks", user?.id] });
    },
  });
}

/** Claims the daily/monthly credit allowance. Safe to call on every app load. */
export function useClaimDailyCredits() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc("claim_daily_credits");
      if (error) throw error;
      return data as { claimed: boolean; balance: number };
    },
    onSuccess: (res) => {
      if (res?.claimed) invalidateWallet(queryClient, user?.id);
    },
  });
}

/** Auto-claims the allowance once per signed-in session load. */
export function useDailyCoinClaim() {
  const { user } = useAuth();
  const claim = useClaimDailyCredits();
  useEffect(() => {
    if (user) claim.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
}

/**
 * Convenience aggregate used by prediction surfaces. Returns whether a given
 * prediction is revealed / strategy-unlocked for the current user, plus actions.
 */
export function useCoins() {
  const { user, isAdmin } = useAuth();
  const wallet = useCreditWallet();
  const founding = useFoundingCreditWindow();
  const unlocks = usePredictionUnlocks();
  const strategyUnlocks = useStrategyUnlocks();
  const unlock = useUnlockPrediction();
  const unlockStrategy = useUnlockStrategy();
  // When crawler indexing is open, every visitor sees full picks + strategy
  // insights so search engines and AI agents can index them. Logged-in users
  // still spend credits normally when they click reveal; this only affects
  // *visibility*, not the credit ledger.
  const indexingOpen = useIndexingOpen();

  const daily = wallet.data?.daily_credits ?? 0;
  const purchased = wallet.data?.purchased_credits ?? 0;
  const earned = wallet.data?.earned_credits ?? 0;
  // Purchased is always spendable; earned is locked during the founding window.
  const spendableNow = daily + purchased + (founding.active ? 0 : earned);

  return {
    isLoggedIn: !!user,
    isAdmin,
    unlimited: isAdmin,
    balance: wallet.data?.balance ?? 0,
    dailyCredits: daily,
    persistentCredits: wallet.data?.persistent_credits ?? 0,
    purchasedCredits: purchased,
    earnedCredits: earned,
    spendableNow: isAdmin ? Infinity : spendableNow,
    lockedEarned: founding.active ? earned : 0,
    isWalletLoading: wallet.isLoading,
    unlockedIds: unlocks.data ?? new Set<string>(),
    isUnlocksLoading: unlocks.isLoading,
    indexingOpen,
    // GATES_SUSPENDED opens every pick and every analysis to every visitor.
    isUnlocked: (predictionId: string) =>
      GATES_SUSPENDED || indexingOpen || isAdmin || (unlocks.data ?? new Set()).has(predictionId),
    isStrategyUnlocked: (predictionId: string) =>
      GATES_SUSPENDED || indexingOpen || isAdmin || (strategyUnlocks.data ?? new Set()).has(predictionId),
    unlock,
    unlockStrategy,
  };
}

export interface CreditPack {
  id: string;
  name: string;
  credits: number;
  amount: number; // minor units (kobo)
  /** USD price in cents, charged to visitors outside Nigeria. */
  amount_usd: number | null;
  currency: string;
  sort_order: number;
}
export type CoinPack = CreditPack;

/** Purchasable credit packs (active only), ordered for display. */
export function useCreditPacks() {
  return useQuery({
    queryKey: ["credit-packs"],
    queryFn: async (): Promise<CreditPack[]> => {
      const { data, error } = await supabase
        .from("credit_packs")
        .select("id, name, credits, amount, amount_usd, currency, sort_order")
        .eq("active", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data as CreditPack[]) ?? [];
    },
  });
}
export const useCoinPacks = useCreditPacks;

/**
 * Starts a Paystack checkout and redirects to the hosted payment page.
 * Pass a `packId` for a preset pack, or `{ credits }` for a custom amount.
 * Credits are only added server-side once payment succeeds.
 */
export function useBuyCredits() {
  return useMutation({
    mutationFn: async (input: string | { credits: number; currency?: "USD" | "NGN" } | { packId: string; currency?: "USD" | "NGN" }) => {
      await ensureValidSession();
      const body =
        typeof input === "string"
          ? { pack_id: input, origin: window.location.origin }
          : "packId" in input
            ? { pack_id: input.packId, currency: input.currency, origin: window.location.origin }
            : { credits: input.credits, currency: input.currency, origin: window.location.origin };
      trackEvent("begin_checkout", { product: "credits", currency: typeof input === "string" ? "USD" : input.currency ?? "USD" });
      const { data, error } = await supabase.functions.invoke("paystack-initialize", {
        body,
      });
      if (error) throw error;
      const url = (data as { authorization_url?: string })?.authorization_url;
      if (!url) throw new Error("Could not start checkout");
      window.location.href = url;
      return data as { authorization_url: string; reference: string };
    },
  });
}
export const useBuyCoins = useBuyCredits;

/** Confirms a returned Paystack reference and returns its final status. */
export function useVerifyCoinPurchase() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (reference: string) => {
      await ensureValidSession();
      return await verifyCreditPurchase({ data: { reference } });
    },
    onSuccess: (res) => {
      if (res?.status === "paid") invalidateWallet(queryClient, user?.id);
      void queryClient.invalidateQueries({ queryKey: ["coin-purchases"] });
      void queryClient.invalidateQueries({ queryKey: ["all-coin-purchases"] });
    },
  });
}
